import React, { useEffect, useState } from "react";
import { RiMenu2Line } from "react-icons/ri";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { cleanCustomer, systemValues, updateSystemValues } from "../utils/api";
import useStore from "../utils/store";
import Confirm2 from "./modals/Confirm2";

const Head = ({ text, onClick, title, menu, reload, hideDatePicker }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mssgEnabled, setMssgEnabled] = useState(false);
  const [mssgLoading, setMssgLoading] = useState(false);
  const user = useStore((state) => state.user);
  const startDate = useStore((state) => state.startDate);
  const endDate = useStore((state) => state.endDate);
  const isStockPage = hideDatePicker || location.pathname.includes("stock");

  const getSystemValues = async () => {
    const [success, res] = await systemValues();
    if (success) {
      setStartDate(res?.startDate?.split("T")[0]);
      setEndDate(res?.endDate?.split("T")[0]);
      setBackDate(res?.backDate);
      if (res?.mssgEnabled !== undefined) setMssgEnabled(Boolean(res.mssgEnabled));
    }
  };

  const updateSystem = async (data) => {
    const [success] = await updateSystemValues(data);
    if (!success) {
      toast.error("System Values Not Updated");
    }
  };

  const setStartDate = useStore((state) => state.setStartDate);
  const setEndDate = useStore((state) => state.setEndDate);
  const setBackDate = useStore((state) => state.setBackDate);

  useEffect(() => {
    getSystemValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200/80 px-4 sm:px-6 py-2.5 shadow-xs flex flex-col md:flex-row justify-between items-center gap-3">
      {/* Left Area: Menu Button, Message Toggle, User Badge */}
      <div className="flex items-center justify-between w-full md:w-auto gap-3">
        <div className="flex items-center gap-2.5">
          <button
            onClick={menu}
            className="bg-slate-100 hover:bg-emerald-50 text-slate-700 hover:text-emerald-700 p-2 rounded-xl transition active:scale-95 border border-slate-200 shadow-xs cursor-pointer flex items-center justify-center"
            title="Toggle Menu"
          >
            <RiMenu2Line className="text-xl" />
          </button>

          {user?.type === "Admin" && (
            <button
              disabled={mssgLoading}
              onClick={async () => {
                setMssgLoading(true);
                const newValue = !mssgEnabled;
                const [success] = await updateSystemValues({ mssgEnabled: newValue });
                setMssgLoading(false);
                if (success) {
                  setMssgEnabled(newValue);
                  toast.success(`Message ${newValue ? "Enabled" : "Disabled"}`);
                } else {
                  toast.error("System Values Not Updated");
                }
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all active:scale-95 cursor-pointer shadow-xs flex items-center gap-1.5 ${
                mssgEnabled
                  ? "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                  : "bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-200"
              }`}
            >
              <span>{mssgEnabled ? "🔔" : "🔕"}</span>
              <span>Message {mssgEnabled ? "Enabled" : "Disabled"}</span>
            </button>
          )}

          <Link
            to={user?.type === "Admin" || user?.type === "ViewAdmin" ? "/admin" : user?.type === "MarketingStaff" ? "/staff/marketing/orders" : "/admin"}
            onClick={() => {
              if (location.pathname === "/admin" && reload) {
                reload();
              }
            }}
            className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-extrabold text-xs px-3 py-1.5 rounded-lg border border-slate-200 shadow-xs flex items-center gap-1 transition active:scale-95 cursor-pointer no-underline"
            title="Dashboard"
          >
            <span>👤</span>
            <span>{title ? title : user?.admin?.userName || user?.staff?.userName || user?.userName || "Admin"}</span>
          </Link>
        </div>

        {/* Mobile Reset button */}
        {location.pathname === "/admin" && user?.type !== "ViewAdmin" && (
          <div className="md:hidden block">
            <button
              onClick={() => setOpen(true)}
              className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs px-3 py-1.5 rounded-lg shadow transition active:scale-95"
            >
              Reset
            </button>
          </div>
        )}
      </div>

      {/* Action Text / Button if passed */}
      {text && (
        <button
          onClick={onClick}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-4 py-2 rounded-lg shadow-sm transition active:scale-95"
        >
          {text}
        </button>
      )}

      {/* Right Area: Navigation Quick Actions & Date Range */}
      <div className="flex items-center gap-3 flex-wrap justify-end w-full md:w-auto">
        {/* Marketing Staff Quick Links */}
        {user?.type === "MarketingStaff" && (
          <div className="flex gap-2 items-center">
            <Link
              to="/staff/marketing/orders"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-3 py-1.5 rounded-lg shadow-xs transition active:scale-95 flex items-center gap-1"
            >
              <span>🛍️</span>
              <span>Orders</span>
            </Link>
            <Link
              to="/staff/marketing/payment"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-3 py-1.5 rounded-lg shadow-xs transition active:scale-95 flex items-center gap-1"
            >
              <span>💰</span>
              <span>Payment</span>
            </Link>
            <Link
              to="/staff/marketing/purchase"
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs px-3 py-1.5 rounded-lg shadow-xs transition active:scale-95 flex items-center gap-1"
            >
              <span>🛒</span>
              <span>Purchase</span>
            </Link>
          </div>
        )}

        {/* Admin & ViewAdmin Quick Links & Date Selector */}
        {(user?.type === "Admin" || user?.type === "ViewAdmin") && (
          <div className="flex gap-2 items-center flex-wrap">
            <Link
              to="/admin/orders"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs px-3 py-1.5 rounded-lg shadow-xs transition active:scale-95 flex items-center gap-1"
            >
              <span>🛍️</span>
              <span>Order</span>
            </Link>
            <Link
              to="/admin/payments"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs px-3 py-1.5 rounded-lg shadow-xs transition active:scale-95 flex items-center gap-1"
            >
              <span>💰</span>
              <span>Payment</span>
            </Link>
            <Link
              to="/admin/purchase"
              className="bg-teal-600 hover:bg-teal-700 text-white font-extrabold text-xs px-3 py-1.5 rounded-lg shadow-xs transition active:scale-95 flex items-center gap-1"
            >
              <span>🛒</span>
              <span>Purchase</span>
            </Link>

            {/* Date Range Selector Pill (Hidden on Stock Management page and for ViewAdmin) */}
            {!isStockPage && user?.type !== "ViewAdmin" && (
              <div className="hidden md:flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs shadow-xs">
                <span className="text-slate-500 pl-1.5 font-bold">📅</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={async (e) => {
                    const val = e.target.value;
                    setStartDate(val);
                    await updateSystem({ startDate: val });
                    window.dispatchEvent(new Event("plabel-data-updated"));
                    localStorage.setItem("plabel_last_sync", Date.now().toString());
                  }}
                  className="bg-white border border-slate-300 text-slate-800 text-xs font-semibold py-1 px-2 rounded-lg outline-none focus:border-emerald-500 shadow-xs"
                  max={endDate}
                />
                <span className="text-slate-400 font-bold px-0.5">to</span>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={async (e) => {
                    const val = e.target.value;
                    setEndDate(val);
                    await updateSystem({ endDate: val });
                    window.dispatchEvent(new Event("plabel-data-updated"));
                    localStorage.setItem("plabel_last_sync", Date.now().toString());
                  }}
                  className="bg-white border border-slate-300 text-slate-800 text-xs font-semibold py-1 px-2 rounded-lg outline-none focus:border-emerald-500 shadow-xs"
                />
              </div>
            )}
          </div>
        )}

        {/* Desktop Reset Button on /admin */}
        {location.pathname === "/admin" && user?.type !== "ViewAdmin" && (
          <div className="md:block hidden">
            <button
              onClick={() => setOpen(true)}
              className="bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold text-xs px-3 py-1.5 rounded-lg shadow-xs transition active:scale-95 flex items-center gap-1 cursor-pointer"
            >
              <span>🔄</span>
              <span>Reset</span>
            </button>
            {open && (
              <Confirm2
                open={open}
                setOpen={setOpen}
                onClick={async () => {
                  const [success, res] = await cleanCustomer();
                  if (success) {
                    toast.success(res);
                    setOpen(false);
                    reload();
                  } else {
                    toast.error(res);
                  }
                }}
              />
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Head;
